import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { DevFlagSelectors } from '@core/dev-flags-data';
import { DevFlag } from '@core/dev-flags-types';
import { Position } from '@core/http-types';
import type {
  InsertFromTemplateMethod,
  OfferData,
  OfferStatus,
  StatusChangeType,
} from '@core/offers-types';
import { PlacementMovement } from '@core/stages-types';

import { JobsWrapperActions } from './jobs-wrapper.actions';
import { JobsBasicActions } from '../jobs-basic/jobs-basic.store';
import { JobsBriefActions } from '../jobs-brief/jobs-brief.store';
import { JobsFullActions } from '../jobs-full/jobs-full.store';
import { JobItemsActions } from '../jobs-item/jobs-item.store';

@Injectable({ providedIn: 'root' })
export class JobsWrapperActionsService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  private readonly hasForceNewOffersEndpointFlag = this.store.selectSignal(
    DevFlagSelectors.isEnabled(DevFlag.ForceNewOffersEndpointFlag),
  );

  public fetchCollectionActionsToDispatch = this.hasForceNewOffersEndpointFlag()
    ? [JobsWrapperActions.fetchCollection()]
    : [JobsWrapperActions.fetchActiveCollection(), JobsWrapperActions.fetchArchivedCollection()];
  public fetchActiveCollectionSuccessActions = this.hasForceNewOffersEndpointFlag()
    ? JobsWrapperActions.fetchCollectionSuccess
    : JobItemsActions.fetchCollectionSuccess;
  public fetchActiveCollectionActions = this.hasForceNewOffersEndpointFlag()
    ? JobsWrapperActions.fetchCollection
    : JobsWrapperActions.fetchActiveCollection;
  public fetchArchivedCollectionActions = this.hasForceNewOffersEndpointFlag()
    ? JobsWrapperActions.fetchCollection
    : JobsWrapperActions.fetchArchivedCollection;
  public briefReferenceAction = this.hasForceNewOffersEndpointFlag()
    ? JobsBasicActions.references
    : JobsBriefActions.references;
  public itemReferenceAction = this.hasForceNewOffersEndpointFlag()
    ? JobsBasicActions.references
    : JobItemsActions.references;
  public fetchFullOneFailedAction = JobsWrapperActions.fetchFullOneFailed;
  public fetchFullOneSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.fetchFullOneSuccess),
  );
  public updateFullSuccessAction = JobsWrapperActions.updateFullSuccess;
  public updateFullFailedAction = JobsWrapperActions.updateFullFailed;
  public jobsFullUpdateSuccessAction = JobsFullActions.updateSuccess;
  public followSuccessAction$ = this.actions$.pipe(ofType(JobsWrapperActions.followSuccess));
  public followSuccessAction = JobsWrapperActions.followSuccess;
  public unfollowSuccessAction$ = this.actions$.pipe(ofType(JobsWrapperActions.unfollowSuccess));
  public unfollowSuccessAction = JobsWrapperActions.unfollowSuccess;
  public duplicateSuccessAction = JobsWrapperActions.duplicateSuccess;
  public duplicateFailedAction = JobsWrapperActions.duplicateFailed;
  public dashboardReloadDataActions$ = this.actions$.pipe(
    ofType(
      JobsWrapperActions.createSuccess,
      JobsWrapperActions.updateFullSuccess,
      JobsWrapperActions.duplicateSuccess,
      JobsWrapperActions.changeStatusSuccess,
      JobsWrapperActions.removeOneSuccess,
    ),
  );
  public createSuccessAction = JobsWrapperActions.createSuccess;
  public createSuccessAction$ = this.actions$.pipe(ofType(JobsWrapperActions.createSuccess));
  public createFailedAction$ = this.actions$.pipe(ofType(JobsWrapperActions.createFailed));
  public createFromRequisitionSuccessAction = JobsWrapperActions.createFromRequisitionSuccess;
  public createFromRequisitionSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.createFromRequisitionSuccess),
  );
  public createFromTemplateSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.createFromTemplateSuccess),
  );
  public createFromPositionSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.createFromPositionSuccess),
  );
  public changeStatusSuccessAction = JobsWrapperActions.changeStatusSuccess;
  public changeStatusSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.changeStatusSuccess),
  );
  public changeStatusFailedAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.changeStatusFailed),
  );
  public previewSuccessAction$ = this.actions$.pipe(ofType(JobsWrapperActions.previewSuccess));
  public previewFailedAction$ = this.actions$.pipe(ofType(JobsWrapperActions.previewFailed));
  public uploadSocialMediaImageSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.uploadSocialMediaImageSuccess),
  );
  public uploadSocialMediaImageFailedAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.uploadSocialMediaImageFailed),
  );
  public uploadCoverImageSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.uploadCoverImageSuccess),
  );
  public uploadCoverImageFailedAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.uploadCoverImageFailed),
  );
  public updateSlugSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.updateSlugSuccess),
  );
  public updateSlugFailedAction$ = this.actions$.pipe(ofType(JobsWrapperActions.updateSlugFailed));
  public insertDataFromTemplateSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.insertDataFromTemplateSuccess),
  );
  public insertDataFromTemplateSuccessAction = JobsWrapperActions.insertDataFromTemplateSuccess;
  public updatePositionsSuccessAction$ = this.actions$.pipe(
    ofType(JobsWrapperActions.updatePositionsSuccess),
  );
  public removeOneSuccessAction$ = this.actions$.pipe(ofType(JobsWrapperActions.removeOneSuccess));
  public removeOneSuccessAction = JobsWrapperActions.removeOneSuccess;
  public removeOneFailedAction$ = this.actions$.pipe(ofType(JobsWrapperActions.removeOneFailed));

  public fetchCollection(): void {
    if (this.hasForceNewOffersEndpointFlag()) {
      this.store.dispatch(JobsWrapperActions.fetchCollection());
    } else {
      this.store.dispatch(JobsWrapperActions.fetchActiveCollection());
      this.store.dispatch(JobsWrapperActions.fetchArchivedCollection());
    }
  }

  public fetchActiveCollection(): void {
    if (this.hasForceNewOffersEndpointFlag()) {
      this.store.dispatch(JobsWrapperActions.fetchCollection());
    } else {
      this.store.dispatch(JobsWrapperActions.fetchActiveCollection());
    }
  }

  public switchJobsToProsemirror(): void {
    this.store.dispatch(JobsWrapperActions.switchJobsToProsemirror());
  }

  public fetchFullOne(id: number | string, langCode?: string): void {
    this.store.dispatch(JobsWrapperActions.fetchFullOne({ payload: { id, langCode } }));
  }

  public jobUpdatedFromWebsocket(id: number) {
    this.store.dispatch(JobsWrapperActions.jobUpdatedFromWebsocket({ payload: { id } }));
  }

  public jobCreatedFromWebsocket(id: number) {
    this.store.dispatch(JobsWrapperActions.jobCreatedFromWebsocket({ payload: { id } }));
  }

  public updateFull(job: OfferData & { id: number }) {
    this.store.dispatch(JobsWrapperActions.updateFull({ payload: { offer: job } }));
  }

  public follow(jobId: number) {
    this.store.dispatch(JobsWrapperActions.follow({ payload: { id: jobId } }));
  }

  public unfollow(jobId: number) {
    this.store.dispatch(JobsWrapperActions.unfollow({ payload: { id: jobId } }));
  }

  public duplicate(jobId: number) {
    this.store.dispatch(JobsWrapperActions.duplicate({ payload: { id: jobId } }));
  }

  public create(job: OfferData) {
    this.store.dispatch(JobsWrapperActions.create({ payload: { offer: job } }));
  }

  public createFromRequisition(requisitionId: number) {
    this.store.dispatch(JobsWrapperActions.createFromRequisition({ payload: { requisitionId } }));
  }

  public createFromTemplate(templateId: number) {
    this.store.dispatch(JobsWrapperActions.createFromTemplate({ payload: { templateId } }));
  }

  public createFromPosition(positionId: number) {
    this.store.dispatch(JobsWrapperActions.createFromPosition({ payload: { positionId } }));
  }

  public changeStatus(id: number, status: StatusChangeType, currentStatus?: OfferStatus) {
    this.store.dispatch(
      JobsWrapperActions.changeStatus({ payload: { id, status, currentStatus } }),
    );
  }

  public preview(offer: OfferData) {
    this.store.dispatch(JobsWrapperActions.preview({ payload: { offer } }));
  }

  public uploadSocialMediaImage(id: number, file: File) {
    this.store.dispatch(JobsWrapperActions.uploadSocialMediaImage({ payload: { id, file } }));
  }

  public uploadCoverImage(id: number, file: File) {
    this.store.dispatch(JobsWrapperActions.uploadCoverImage({ payload: { id, file } }));
  }

  public updateSlug(id: number, slug: string) {
    this.store.dispatch(JobsWrapperActions.updateSlug({ payload: { id, slug } }));
  }

  public insertDataFromTemplate(payload: {
    offerId: number;
    jobTemplateId: number;
    insertMethod: InsertFromTemplateMethod;
    placementsMovements?: PlacementMovement[];
  }) {
    this.store.dispatch(JobsWrapperActions.insertDataFromTemplate({ payload }));
  }

  public updatePositions(positions: Position[]) {
    this.store.dispatch(JobsWrapperActions.updatePositions({ payload: positions }));
  }

  public jobDeletedFromWebsocket(id: number) {
    this.store.dispatch(JobsWrapperActions.jobDeletedFromWebsocket({ payload: { id } }));
  }

  public jobsUpdatedFromWebsocket() {
    this.store.dispatch(JobsWrapperActions.jobsUpdatedFromWebsocket());
  }

  public removeOne(id: number) {
    this.store.dispatch(JobsWrapperActions.removeOne({ payload: { id } }));
  }
}
